import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./Game.css";
import desktopBg from "../images/desktop/desktop.png";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getLobbyGameData } from "../../functions/getLobbyGameData";
import { saveLobbyGameProgress } from "../../functions/saveLobbyGameProgress";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";

/* ─── Helpers ──────────────────────────────────────────────── */
function normalizeAnswer(s) {
  return String(s || "").trim().toLowerCase();
}

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Parse a "HH:MM:SS" DB time string (as saved by saveLobbyGameProgress) back to seconds. */
function parseHHMMSSToSeconds(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d+):(\d{1,2}):(\d{1,2})$/);
  if (!m) return 0;
  const [, h, mi, se] = m;
  const total = Number(h) * 3600 + Number(mi) * 60 + Number(se);
  return Number.isFinite(total) ? Math.max(0, total) : 0;
}

function calcPoints(basePoints, hintLevel) {
  const factors = [1, 0.75, 0.5, 0.25];
  const factor  = factors[Math.min(hintLevel - 1, 3)] ?? 0.25;
  return Math.round(Number(basePoints) * factor);
}

function normalizeBool(v) {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return false;
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

function seedFromString(str) {
  let h = 2166136261;
  const s = String(str ?? "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleWithSeed(arr, seedStr) {
  const a = Array.isArray(arr) ? [...arr] : [];
  const rand = mulberry32(seedFromString(seedStr));
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ─── Confetti canvas ──────────────────────────────────────── */
const CONFETTI_COLORS = ["#4f9ef8","#2563eb","#34d399","#fbbf24","#f472b6","#a78bfa","#fb923c"];
const SWAL_BG = {
  background: `url(${desktopBg}) center / cover no-repeat`,
  color: "#1e3a8a",
};

function Confetti({ active }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const setCanvasSize = () => {
      const w = window.innerWidth;
      const h = window.visualViewport?.height || window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };

    if (!active) {
      cancelAnimationFrame(rafRef.current);
      particles.current = [];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    setCanvasSize();

    const onResize = () => setCanvasSize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.visualViewport?.addEventListener?.("resize", onResize);

    particles.current = Array.from({ length: 180 }, () => ({
      x:     Math.random() * canvas.width,
      y:     -20 - Math.random() * 60,
      r:     4 + Math.random() * 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      vx:    (Math.random() - 0.5) * 6,
      vy:    2.5 + Math.random() * 5,
      rot:   Math.random() * 360,
      rotV:  (Math.random() - 0.5) * 10,
      shape: Math.random() > 0.5 ? "rect" : "circle",
      alpha: 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.rot += p.rotV; p.vy += 0.1;
        p.alpha = Math.max(0, p.alpha - 0.007);
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") { ctx.fillRect(-p.r, -p.r * 0.5, p.r * 2, p.r); }
        else { ctx.beginPath(); ctx.arc(0, 0, p.r, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      });
      particles.current = particles.current.filter((p) => p.alpha > 0 && p.y < canvas.height + 60);
      if (particles.current.length > 0) rafRef.current = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.visualViewport?.removeEventListener?.("resize", onResize);
    };
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999,
        opacity: active ? 1 : 0, transition: "opacity 0.4s",
      }}
    />
  );
}

/* ─── Keyframes injection (once) ──────────────────────────── */
function injectKeyframes() {
  const id = "game-extra-keyframes";
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = `
    @keyframes shake {
      0%,100%{transform:translateX(0)}
      20%{transform:translateX(-8px)} 40%{transform:translateX(8px)}
      60%{transform:translateX(-5px)} 80%{transform:translateX(5px)}
    }
    @keyframes popIn {
      0%{transform:scale(0.95)} 60%{transform:scale(1.04)} 100%{transform:scale(1)}
    }
    @keyframes fadeUp {
      from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)}
    }
    @keyframes pointsPop {
      0%  {opacity:0;transform:translate(-50%,-50%) scale(0.55)}
      55% {opacity:1;transform:translate(-50%,-50%) scale(1.1)}
      80% {transform:translate(-50%,-50%) scale(0.97)}
      100%{opacity:1;transform:translate(-50%,-50%) scale(1)}
    }
    @keyframes timerUrgent {
      from{transform:scale(1)} to{transform:scale(1.07)}
    }
  `;
  document.head.appendChild(s);
}

/* ─── Remaining inline styles (non-responsive, kept as-is) ─── */
const S = {
  page:  { minHeight: "90vh", padding: "15px 18px 70px", fontFamily: "'Nunito', sans-serif" },
  inner: { maxWidth: 980, margin: "0 auto" },

  topRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 8, flexWrap: "wrap" },
  topLeft: { color: "#1a2b5e", fontWeight: 900, fontSize: 18 },

  scoreChip: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "rgba(37,99,235,0.10)", borderRadius: 999,
    padding: "6px 18px", color: "#2563eb", fontWeight: 900, fontSize: 22,
  },

  timerWrap: (urgent) => ({
    minWidth: 82, height: 48, borderRadius: 999, padding: "0 16px",
    background: urgent ? "rgba(239,68,68,0.10)" : "rgba(255,255,255,0.72)",
    border: `2px solid ${urgent ? "rgba(239,68,68,0.45)" : "rgba(255,255,255,0.9)"}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: urgent ? "0 6px 20px rgba(239,68,68,0.18)" : "0 8px 22px rgba(100,120,200,0.14)",
    color: urgent ? "#ef4444" : "#2563eb",
    fontWeight: 900, fontSize: 17, fontVariantNumeric: "tabular-nums", letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    animation: urgent ? "timerUrgent 0.5s ease-in-out infinite alternate" : "none",
  }),

  title: {
    textAlign: "center", fontSize: 48, fontWeight: 900, color: "#2563eb",
    margin: "20px 0 22px", textShadow: "0 2px 18px rgba(37,99,235,0.18)",
  },

  hintBtn: (enabled, isLatest) => ({
    width: "min(520px, 100%)", padding: "14px 16px", borderRadius: 14,
    border: `1.5px solid ${isLatest ? "rgba(37,99,235,0.30)" : "rgba(255,255,255,0.92)"}`,
    background: enabled ? (isLatest ? "rgba(37,99,235,0.07)" : "rgba(255,255,255,0.78)") : "rgba(255,255,255,0.30)",
    boxShadow: enabled ? "0 10px 26px rgba(100,120,200,0.10)" : "none",
    color: enabled ? "#1a2b5e" : "rgba(26,43,94,0.30)",
    fontWeight: 900, display: "flex", alignItems: "center", gap: 12,
    opacity: enabled ? 1 : 0.65, transition: "all 0.4s ease",
    cursor: "default", userSelect: "none",
  }),

  hintIdx: (isLatest) => ({
    width: 32, height: 32, borderRadius: 999, flexShrink: 0,
    background: isLatest ? "rgba(37,99,235,0.18)" : "rgba(37,99,235,0.10)",
    color: "#2563eb", fontWeight: 900,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.3s",
  }),

  ptsBadge: (isLatest) => ({
    marginLeft: "auto", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap",
    color: isLatest ? "#2563eb" : "rgba(37,99,235,0.45)",
    background: isLatest ? "rgba(37,99,235,0.10)" : "rgba(37,99,235,0.04)",
    borderRadius: 999, padding: "3px 10px", transition: "all 0.3s",
  }),

  pointsPopup: {
    position: "fixed", top: "36%", left: "50%",
    transform: "translate(-50%,-50%)",
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white", fontWeight: 900, fontSize: 30,
    padding: "18px 40px", borderRadius: 20,
    boxShadow: "0 20px 60px rgba(37,99,235,0.45)",
    zIndex: 10000, animation: "pointsPop 0.65s cubic-bezier(0.34,1.56,0.64,1) forwards",
    pointerEvents: "none", whiteSpace: "nowrap",
  },
};

/* ─── Game component ───────────────────────────────────────── */
export default function Game() {
  const location   = useLocation();
  const navigate   = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const dispatch   = useDispatch();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    // Keep header back button on the same page while playing.
    dispatch(setBackButtonUrl("/game"));
  }, [dispatch]);

  // Keep user on Game page even when browser back is pressed.
  useEffect(() => {
    const push = () => {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch {
        // ignore
      }
    };
    // Add one extra entry so "Back" triggers popstate here.
    push();
    const onPopState = (e) => {
      e?.preventDefault?.();
      push();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch { return {}; }
  }, []);

  const isAnchorMode = useMemo(() => {
    if (location.state?.isAnchorMode) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch { return false; }
  }, [location.state?.isAnchorMode]);

  const uid =
    user?.userId ??
    user?.userid ??
    user?.id ??
    storedUserData.userId ??
    storedUserData.userid ??
    storedUserData.id;

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid =
      user?.sessionId ??
      user?.sessionid ??
      storedUserData.sessionId;
    const oid =
      user?.organizationId ??
      user?.orgId ??
      user?.organizationid ??
      storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData, user]);

  const lobbyName = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_name) return String(st.lobby_name);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_name) return String(cached.lobby_name);
    } catch { /* ignore */ }
    if (storedUserData?.lobby) return String(storedUserData.lobby);
    return "";
  }, [location.state, storedUserData?.lobby]);

  const lobbyPin = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_pin) return String(st.lobby_pin);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_pin) return String(cached.lobby_pin);
    } catch { /* ignore */ }
    const ln = String(lobbyName || "");
    const m = ln.match(/LOBBY-(\d{5})/i);
    if (m && m[1]) return String(m[1]);
    return "";
  }, [location.state, lobbyName]);

  const [activeLobbyName, setActiveLobbyName] = useState("");
  useEffect(() => {
    if (lobbyName && String(lobbyName).trim() !== "") setActiveLobbyName(String(lobbyName).trim());
  }, [lobbyName]);

  const reportIdRef               = useRef(null);
  const reportCreatePromiseRef    = useRef(null);
  const reportStartSentRef        = useRef(false);
  const demoPopupShownRef         = useRef(false);
  const timeUpHandledRef          = useRef(false);
  const attemptsRef               = useRef([]);

  const showRevealAnswerPopup = useCallback((correctAnswer, title) => {
    const ans = String(correctAnswer ?? "").trim() || "—";
    Swal.fire({
      title: title || "Answer",
      html: `<b>${ans}</b>`,
      timer: 3000,
      timerProgressBar: true,
      showConfirmButton: false,
      ...SWAL_BG,
    });
  }, []);

  useEffect(() => {
    // Applies to guests too now — this used to bail out for guest users
    // entirely, so a guest's reportId was never restored from anything on
    // mount and every refresh created a brand-new report row (duplicate
    // leaderboard entries for the same play session). The DB-sourced value
    // fetched in the main load effect below is the authoritative fallback;
    // this just covers the case where the client already has it cached.
    const rid =
      (user?.reportId != null && String(user.reportId).trim() !== "")
        ? String(user.reportId).trim()
        : (storedUserData?.reportId != null && String(storedUserData.reportId).trim() !== "")
          ? String(storedUserData.reportId).trim()
          : null;
    if (rid) reportIdRef.current = rid;
  }, [user?.reportId, storedUserData?.reportId]);

  const isDemoBypass = useMemo(() => {
    const role = String(user?.role ?? storedUserData?.role ?? storedUserData?.user_type ?? "").trim().toLowerCase();
    const src  = String(user?.source ?? storedUserData?.source ?? "").trim().toLowerCase();
    const sid  = String(user?.sessionId ?? storedUserData?.sessionId ?? "").trim().toLowerCase();
    const oid  = String(user?.organizationId ?? storedUserData?.organizationId ?? "").trim().toLowerCase();
    return (
      role === "demobypass" ||
      src === "demo" ||
      (sid === "demobypass" && oid === "demobypass") ||
      sid.startsWith("demo")
    );
  }, [user, storedUserData]);

  const pushGameReport = useCallback(async (overrides) => {
    if (isDemoBypass) return;
    if (!uid) return;
    if (!tokenForApi) return;

    const o          = overrides && typeof overrides === "object" ? overrides : {};
    const pointsVal  = Number.isFinite(Number(o.points)) ? Number(o.points) : 0;
    const timeSec    = Number.isFinite(Number(o.time))   ? Number(o.time)   : 0;
    const timeText   = fmtTime(Math.max(0, Math.floor(timeSec)));

    const roleUpper   = String(user?.role ?? storedUserData?.role ?? storedUserData?.user_type ?? "").trim().toUpperCase();
    const sourceUpper = String(user?.source ?? storedUserData?.source ?? "").trim().toUpperCase();
    const isGuest     = roleUpper === "GUEST_USER" || sourceUpper === "GUEST_USER";

    const useRegisteredReportApi =
      (roleUpper === "ORG_USER" || sourceUpper === "ORG_USER") &&
      user?.token != null &&
      String(user.token).trim() !== "";

    const token = String(user?.token ?? storedUserData?.token ?? "").trim();

    const basePayload = {
      sessionId: user?.sessionId ?? storedUserData.sessionId,
      role:      user?.role ?? storedUserData.role ?? storedUserData.user_type,
      token,
      name: (() => {
        const direct = user?.name ?? storedUserData.name;
        if (direct != null && String(direct).trim() !== "") return String(direct).trim();
        const em = user?.email ?? storedUserData.email;
        if (em != null && String(em).trim() !== "") {
          const s      = String(em).trim();
          const prefix = s.includes("@") ? s.split("@")[0] : s;
          if (prefix.trim() !== "") return prefix.trim();
        }
        return "Guest";
      })(),
      email:          user?.email ?? storedUserData.email ?? null,
      userId:         uid,
      gameId:         user?.gameId ?? storedUserData.gameId ?? "",
      organizationId: user?.organizationId ?? storedUserData.organizationId,
      points:         pointsVal,
      time:           timeText,
      ans:            activeLobbyName ? `GUESS_ME:${String(activeLobbyName)}` : "GUESS_ME",
      user_type:      user?.role ?? storedUserData.role ?? storedUserData.user_type,
      owner:          user?.role ?? storedUserData.role ?? storedUserData.user_type,
      guestScoreMinimal: isGuest,
      ...(useRegisteredReportApi ? { useRegisteredReportApi: true } : {}),
    };

    let ridForSend =
      (reportIdRef.current != null && String(reportIdRef.current).trim() !== "")
        ? String(reportIdRef.current).trim()
        : "";

    if (!ridForSend && !reportCreatePromiseRef.current) {
      reportCreatePromiseRef.current = (async () => {
        const created   = await sendReport({ ...basePayload, points: 0, time: "00:00", reportId: null });
        const createdId = extractReportIdFromResponse(created, uid);
        if (createdId && String(createdId).trim() !== "") {
          const clean = String(createdId).trim();
          reportIdRef.current = clean;
          try {
            await updateGameSettings({
              token: tokenForApi, userId: uid,
              themeName: storedUserData.themeName || user?.themeName || "guess_me",
              reportid: clean,
              lobby_name: activeLobbyName || lobbyName || null,
            });
          } catch { /* ignore */ }
          return clean;
        }
        return "";
      })().finally(() => { reportCreatePromiseRef.current = null; });
      ridForSend = await reportCreatePromiseRef.current;
    }

    try {
      const res   = await sendReport({ ...basePayload, reportId: ridForSend || null });
      const newId = extractReportIdFromResponse(res, uid);
      if (newId && String(newId).trim() !== "") {
        const clean = String(newId).trim();
        if (clean !== ridForSend) {
          reportIdRef.current = clean;
          try {
            await updateGameSettings({
              token: tokenForApi, userId: uid,
              themeName: storedUserData.themeName || user?.themeName || "guess_me",
              reportid: clean,
              lobby_name: activeLobbyName || lobbyName || null,
            });
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      console.warn("[Game] sendReport failed", e?.response?.status, e?.response?.data ?? e?.message ?? e);
    }
  }, [isDemoBypass, uid, tokenForApi, user, storedUserData, activeLobbyName]);

  /* ── core state ── */
  const [loading,          setLoading         ] = useState(true);
  const [timeDuration,     setTimeDuration    ] = useState(1);
  const [pointsPerCorrect, setPointsPerCorrect] = useState(0);
  const [questions,        setQuestions       ] = useState([]);
  const [idx,              setIdx             ] = useState(0);
  const [typed,            setTyped           ] = useState("");
  const [score,            setScore           ] = useState(0);
  const [attempts,         setAttempts        ] = useState([]);

  useEffect(() => { attemptsRef.current = attempts; }, [attempts]);

  const [answerState,          setAnswerState         ] = useState("idle");
  const [confetti,             setConfetti            ] = useState(false);
  const [pointsEarned,         setPointsEarned        ] = useState(null);
  const [showPopup,            setShowPopup           ] = useState(false);
  const [alreadyCorrect,       setAlreadyCorrect      ] = useState(false);
  const [anchorAnswerRevealed, setAnchorAnswerRevealed] = useState(false);

  const totalElapsedRef  = useRef(0);
  const questionStartRef = useRef(Date.now());
  const intervalRef      = useRef(null);
  const [remaining,   setRemaining  ] = useState(60);
  const [revealCount, setRevealCount] = useState(1);
  const answerInputRef = useRef(null);
  const [stageId, setStageId] = useState(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      return cached?.stageId != null && String(cached.stageId).trim() !== "" ? String(cached.stageId).trim() : null;
    } catch {
      return null;
    }
  });

  const focusAnswerInput = useCallback(() => {
    const el = answerInputRef.current;
    if (!el || alreadyCorrect) return;
    el.focus();
    const len = String(el.value || "").length;
    try { el.setSelectionRange(len, len); } catch { /* ignore */ }
    try { el.scrollIntoView({ block: "center", inline: "nearest" }); } catch { /* ignore */ }
  }, [alreadyCorrect]);

  useEffect(() => {
    const setVv = () => {
      const h = window.visualViewport?.height || window.innerHeight || 0;
      if (h > 0) document.documentElement.style.setProperty("--vvh", `${h}px`);
    };
    setVv();
    window.addEventListener("resize", setVv);
    window.addEventListener("orientationchange", setVv);
    window.visualViewport?.addEventListener?.("resize", setVv);
    return () => {
      window.removeEventListener("resize", setVv);
      window.removeEventListener("orientationchange", setVv);
      window.visualViewport?.removeEventListener?.("resize", setVv);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("game-static-bg");
    document.body.classList.add("game-static-bg");
    return () => {
      document.documentElement.classList.remove("game-static-bg");
      document.body.classList.remove("game-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const stage = await updateGameSettings({
          token: tokenForApi, userId: uid,
          ...(stageId ? { stageId } : {}),
          themeName: storedUserData.themeName || user?.themeName || "guess_me",
          show_question: true,
          lobby_name: lobbyName || storedUserData?.lobby || null,
          ...(lobbyPin ? { lobby_pin: lobbyPin } : {}),
          email:      storedUserData?.email ?? user?.email ?? null,
          fullName:   storedUserData?.name ?? user?.name ?? storedUserData?.fullName ?? null,
          employeeId: storedUserData?.employeeId ?? user?.employeeId ?? null,
          user_type:  storedUserData?.role ?? storedUserData?.user_type ?? user?.role ?? user?.user_type ?? null,
        });
        if (stage?.stageId != null && String(stage.stageId).trim() !== "") {
          const nextStageId = String(stage.stageId).trim();
          setStageId(nextStageId);
          try {
            const prev = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
            sessionStorage.setItem("gameSettings", JSON.stringify({ ...prev, stageId: nextStageId }));
          } catch {
            // ignore
          }
        }
        const resolvedLobby =
          (lobbyName && String(lobbyName).trim() !== "")
            ? String(lobbyName).trim()
            : (stage?.lobby_name != null ? String(stage.lobby_name).trim() : "");
        if (!resolvedLobby) throw new Error("Missing lobby_name");
        setActiveLobbyName(resolvedLobby);

        const data = await getLobbyGameData({
          token: tokenForApi, userId: uid,
          themeName: storedUserData.themeName || user?.themeName || "guess_me",
          lobby_name: resolvedLobby,
        });
        if (cancelled) return;

        const stageStatus = String(data?.status || "").trim().toLowerCase();
        if (stageStatus === "completed") {
          navigate("/thankyou", { replace: true, state: { lobby_name: resolvedLobby, isAnchorMode: isAnchorMode === true } });
          return;
        }

        const dur = Number(data?.time_duration);
        setTimeDuration(Number.isFinite(dur) && dur > 0 ? dur : 1);
        const pts = Number(data?.points);
        setPointsPerCorrect(Number.isFinite(pts) ? pts : 0);
        const shuffleOn     = normalizeBool(stage?.shuffle_questions ?? data?.shuffle_questions);
        const rawQuestions  = Array.isArray(data?.questions) ? data.questions : [];
        const ordered       = shuffleOn ? shuffleWithSeed(rawQuestions, `${resolvedLobby}|${uid}`) : rawQuestions;
        setQuestions(ordered);

        try {
          if (rawQuestions.length > 0) {
            await updateGameSettings({
              token: tokenForApi, userId: uid,
              ...(stageId ? { stageId } : {}),
              themeName: storedUserData.themeName || user?.themeName || "guess_me",
              questions_no: String(rawQuestions.length),
              lobby_name: resolvedLobby,
              ...(lobbyPin ? { lobby_pin: lobbyPin } : {}),
            });
          }
        } catch { /* ignore */ }

        // Resume-after-refresh: `answers` is persisted after every resolved
        // question now (see persistProgress), so a returning player picks up
        // right where they left off instead of restarting at question 1.
        // The shuffle above is seeded by lobby+user, so the same resume
        // index still points at the same question they were on.
        let resumeAttempts = [];
        try {
          const parsedAnswers = data?.answers != null ? JSON.parse(data.answers) : null;
          if (Array.isArray(parsedAnswers)) resumeAttempts = parsedAnswers;
        } catch { /* ignore */ }

        // Reuse the stage row's reportid whenever we have one — a report is
        // created the moment the game loads (before question 1 is even
        // answered), so requiring saved `answers` too was too strict: a
        // refresh in that window (no answers yet) wrongly cleared it and
        // caused a second report to be created. sendReport now self-heals
        // if this id ever turns out to be stale/invalid (falls back to
        // creating a fresh one), so trusting it here is safe either way.
        if (data?.reportid) {
          reportIdRef.current = String(data.reportid).trim();
        }

        if (resumeAttempts.length > 0 && resumeAttempts.length < ordered.length) {
          // Genuinely resuming an in-progress attempt — restore where they
          // left off. The shuffle above is seeded by lobby+user, so the
          // resume index still points at the same question they were on.
          const resumeScore = Number(data?.total_score);
          setAttempts(resumeAttempts);
          setIdx(resumeAttempts.length);
          setScore(Number.isFinite(resumeScore) ? resumeScore : 0);
          totalElapsedRef.current = parseHHMMSSToSeconds(data?.time);
        } else {
          setIdx(0); setScore(0); setAttempts([]);
          totalElapsedRef.current = 0;
        }
        reportStartSentRef.current = false;
      } catch (e) {
        if (!cancelled) await Swal.fire({ icon: "error", title: "Error", text: e?.message || "Failed to load game", ...SWAL_BG });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenForApi, uid, lobbyName, storedUserData.themeName, user?.themeName]);

  const current = questions[idx] || null;

  useEffect(() => {
    if (loading) return;
    if (!uid || !tokenForApi) return;
    if (!activeLobbyName) return;
    if (!questions || questions.length === 0) return;
    if (reportStartSentRef.current) return;
    reportStartSentRef.current = true;
    // On a fresh game this is 0/0; on a resumed session (refresh mid-game)
    // `score`/`totalElapsedRef` already reflect the progress restored from
    // the DB, so the external report isn't clobbered back down to zero.
    void pushGameReport({ points: score, time: totalElapsedRef.current });
  }, [loading, uid, tokenForApi, activeLobbyName, questions, score, pushGameReport]);

  const perQuestionTotalSec = useMemo(
    () => Math.max(1, Math.round(Number(timeDuration) * 60)),
    [timeDuration]
  );

  const advance = useCallback(async (finalAttempts, finalScore) => {
    clearInterval(intervalRef.current);
    const isLast = idx >= questions.length - 1;
    if (!isLast) { setIdx((p) => p + 1); return; }

    try {
      await saveLobbyGameProgress({
        token: tokenForApi, userId: uid,
        stageId,
        themeName: storedUserData.themeName || user?.themeName || "guess_me",
        lobby_name: activeLobbyName || lobbyName,
        total_score: finalScore, time_sec: totalElapsedRef.current, answers: finalAttempts,
        completed: true, // only the true final answer marks the stage done
      });
    } catch { /* ignore */ }

    try { await pushGameReport({ points: finalScore, time: totalElapsedRef.current }); }
    catch { /* ignore */ }

    const correctCount = Array.isArray(finalAttempts) ? finalAttempts.filter((a) => a && a.correct === true).length : 0;
    const totalCount   = Array.isArray(questions) ? questions.length : 0;
    navigate("/thankyou", {
      replace: true,
      state: {
        lobby_name: activeLobbyName || lobbyName,
        points: finalScore, time: totalElapsedRef.current,
        correct: correctCount, total: totalCount,
        isAnchorMode: isAnchorMode === true,
      },
    });
  }, [idx, questions, tokenForApi, uid, activeLobbyName, lobbyName, storedUserData.themeName, user?.themeName, navigate, isAnchorMode, pushGameReport]);

  // Persist progress after EVERY resolved question (not just at game end) so
  // a refresh/return can resume from where the player left off instead of
  // restarting from question 1. completed:false is critical here — the
  // backend used to always mark the stage 'completed' on this call, which
  // would have bounced a refreshing player straight to the results page
  // after their very first answer.
  const persistProgress = useCallback((nextAttempts, nextScore, nextTimeSec) => {
    if (!uid || !tokenForApi) return;
    saveLobbyGameProgress({
      token: tokenForApi, userId: uid,
      stageId,
      themeName: storedUserData.themeName || user?.themeName || "guess_me",
      lobby_name: activeLobbyName || lobbyName,
      total_score: nextScore, time_sec: nextTimeSec, answers: nextAttempts,
      completed: false,
    }).catch(() => { /* ignore */ });
  }, [uid, tokenForApi, stageId, storedUserData.themeName, user?.themeName, activeLobbyName, lobbyName]);

  useEffect(() => {
    if (!current || loading) return;
    questionStartRef.current = Date.now();
    setTyped(""); setRemaining(perQuestionTotalSec); setRevealCount(isAnchorMode ? 1 : 1);
    setAnswerState("idle"); setAlreadyCorrect(false);
    setAnchorAnswerRevealed(false);
    setConfetti(false); setPointsEarned(null); setShowPopup(false);
    timeUpHandledRef.current = false;
    clearInterval(intervalRef.current);

    if (isAnchorMode) return;

    intervalRef.current = window.setInterval(() => {
      const elapsed = Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000));
      const rem     = Math.max(0, perQuestionTotalSec - elapsed);
      setRemaining(rem);
      const q  = perQuestionTotalSec / 4;
      const rc = elapsed >= 3 * q ? 4 : elapsed >= 2 * q ? 3 : elapsed >= q ? 2 : 1;
      setRevealCount(rc);

      if (rem <= 0) {
        clearInterval(intervalRef.current);
        if (timeUpHandledRef.current) return;
        timeUpHandledRef.current = true;
        const entry = {
          questionId: current.id, question: current.question_name,
          typed: "", correct: false, correct_answer: current.correct_answer,
          timeTakenSec: perQuestionTotalSec, points: 0,
        };
        const next = [...(attemptsRef.current || []), entry];
        setAttempts(next);
        totalElapsedRef.current += perQuestionTotalSec;
        persistProgress(next, score, totalElapsedRef.current);
        Swal.fire({
          title: "Times up",
          html: `Answer: <b>${String(current.correct_answer ?? "").trim() || "—"}</b>`,
          timer: 3000, timerProgressBar: true, showConfirmButton: false, ...SWAL_BG,
        }).then(() => { advance(next, score); });
      }
    }, 250);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, perQuestionTotalSec, loading, isAnchorMode]);

  useEffect(() => {
    if (loading || isAnchorMode || !current) return;
    const id = window.requestAnimationFrame(() => { focusAnswerInput(); });
    return () => window.cancelAnimationFrame(id);
  }, [idx, current, loading, isAnchorMode, focusAnswerInput]);

  const handleSubmit = useCallback(() => {
    if (!current || alreadyCorrect) return;
    const typedVal = String(typed || "").trim();
    if (!typedVal) return;

    if (isDemoBypass && !demoPopupShownRef.current) {
      demoPopupShownRef.current = true;
      window.setTimeout(() => {
        Swal.fire({
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK", allowOutsideClick: false, allowEscapeKey: false, ...SWAL_BG,
        }).then(() => {
          const base = process.env.REACT_APP_BASE_URL || "";
          window.location.href = `${base}/plans`;
        });
      }, 250);
      return;
    }

    const elapsed    = Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000));
    const isCorrect  = normalizeAnswer(typedVal) === normalizeAnswer(current.correct_answer);
    const timeSoFarSec = totalElapsedRef.current + Math.min(elapsed, perQuestionTotalSec);

    if (isCorrect) {
      clearInterval(intervalRef.current);
      setAlreadyCorrect(true);
      const earned    = calcPoints(pointsPerCorrect, revealCount);
      const nextScore = score + earned;
      setScore(nextScore);
      setAnswerState("correct");
      setPointsEarned(earned);
      setConfetti(true);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 1800);
      setTimeout(() => setConfetti(false), 3600);
      void pushGameReport({ points: nextScore, time: timeSoFarSec });
      const entry = {
        questionId: current.id, question: current.question_name,
        typed: typedVal, correct: true, correct_answer: current.correct_answer,
        timeTakenSec: elapsed, points: earned,
      };
      setAttempts((prev) => {
        const next = [...prev, entry];
        totalElapsedRef.current += Math.min(elapsed, perQuestionTotalSec);
        persistProgress(next, nextScore, totalElapsedRef.current);
        setTimeout(() => advance(next, nextScore), 2300);
        return next;
      });
    } else {
      setAnswerState("wrong");
      void pushGameReport({ points: score, time: timeSoFarSec });
      window.requestAnimationFrame(() => focusAnswerInput());
      setTimeout(() => {
        setAnswerState("idle");
        setTyped("");
        window.requestAnimationFrame(() => focusAnswerInput());
      }, 950);
    }
  }, [current, alreadyCorrect, typed, isDemoBypass, revealCount, score, pointsPerCorrect, perQuestionTotalSec, advance, pushGameReport, persistProgress, focusAnswerInput]);

  const handleKeyDown = useCallback(
    (e) => { if (e.key === "Enter") handleSubmit(); },
    [handleSubmit]
  );

  const handleNextHint = useCallback(() => {
    if (revealCount < 4) {
      setRevealCount((r) => r + 1);
    } else {
      if (!anchorAnswerRevealed) {
        showRevealAnswerPopup(current?.correct_answer, "Answer");
        setAnchorAnswerRevealed(true);
      }
      setConfetti(true);
      setTimeout(() => setConfetti(false), 2000);
      setAttempts((prev) => {
        const entry = {
          questionId: current.id, question: current.question_name,
          typed: "", correct: false, correct_answer: current.correct_answer,
          timeTakenSec: 0, points: 0,
        };
        const next = [...prev, entry];
        setTimeout(() => advance(next, score), 1500);
        return next;
      });
    }
  }, [revealCount, current, score, advance, anchorAnswerRevealed, showRevealAnswerPopup]);

  const handleRevealAnswer = useCallback(() => {
    if (!current) return;
    setAnchorAnswerRevealed(true);
    showRevealAnswerPopup(current?.correct_answer, "Answer");
    setConfetti(true);
    setTimeout(() => setConfetti(false), 1200);
    setRevealCount(4);
    setAttempts((prev) => {
      const entry = {
        questionId: current.id, question: current.question_name,
        typed: "", correct: false, correct_answer: current.correct_answer,
        timeTakenSec: 0, points: 0,
      };
      const next = [...prev, entry];
      setTimeout(() => advance(next, score), 400);
      return next;
    });
  }, [current, score, advance, showRevealAnswerPopup]);

  /* ─── guards ── */
  if (loading) return (
    <div style={S.page}><div style={S.inner}>
      <div style={{ textAlign: "center", fontWeight: 900, color: "#1a2b5e", paddingTop: 80 }}>Loading…</div>
    </div></div>
  );
  if (!current) return (
    <div style={S.page}><div style={S.inner}>
      <div style={{ textAlign: "center", fontWeight: 900, color: "#1a2b5e", paddingTop: 80 }}>No questions found.</div>
    </div></div>
  );

  /* ─── derived values ── */
  const hints      = [current.option_one, current.option_two, current.option_three, current.option_four]
    .map((x) => String(x || "").trim());
  const urgent     = remaining <= 10;
  const hintPoints = [1, 2, 3, 4].map((hl) => calcPoints(pointsPerCorrect, hl));

  /* ─── render ── */
  return (
    <>
      <Confetti active={confetti} />

      {showPopup && (
        <div style={S.pointsPopup}>+{pointsEarned} pts 🎉</div>
      )}

      <div style={S.page}>
        <div style={S.inner}>

          {/* Top bar */}
          {!isAnchorMode && (
            <div style={S.topRow}>
              <div style={S.topLeft}>Question {idx + 1} / {questions.length}</div>
              <div style={S.scoreChip}>🏆 {score} pts</div>
              <div style={S.timerWrap(urgent)}>{fmtTime(remaining)}</div>
            </div>
          )}

          {isAnchorMode && (
            <div style={S.topRow}>
              <div style={S.topLeft}>Question {idx + 1} / {questions.length}</div>
            </div>
          )}

          {/* Title — same source as admin/API (`question_name`) */}
          <div className="game-title">
            {String(current?.question_name ?? "").trim() || "Guess me if you can"}
          </div>

          {/* Hints */}
          <div className="game-hint-col">
            {hints.map((h, i) => {
              const enabled  = i < revealCount;
              const isLatest = enabled && i === revealCount - 1;
              return (
                <div key={i} style={S.hintBtn(enabled, isLatest)}>
                  <div style={S.hintIdx(isLatest)}>{i + 1}</div>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    {enabled ? h || "—" : "Unlocking…"}
                  </div>
                  {enabled && !isAnchorMode && (
                    <div style={S.ptsBadge(isLatest)}>
                      {hintPoints[i]} pts
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Anchor mode buttons */}
          {isAnchorMode && (
            <div className="game-anchor-btns">
              <button
                type="button"
                onClick={handleRevealAnswer}
                className="game-anchor-btn"
                style={{
                  background: "linear-gradient(135deg,#60a5fa,#3b82f6)",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(59,130,246,0.35)",
                }}
              >
                Reveal Answer
              </button>
              <button
                type="button"
                onClick={handleNextHint}
                className="game-anchor-btn"
                style={{
                  background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
                  color: "white",
                  boxShadow: "0 10px 30px rgba(37,99,235,0.35)",
                }}
              >
                {revealCount < 4 ? "Next Hint" : "Next Question"}
              </button>
            </div>
          )}

          {/* Input row — CSS class handles responsive layout */}
          {!isAnchorMode && (
            <>
              <div className="game-input-row" data-state={answerState}>
                <input
                  ref={answerInputRef}
                  className="game-input"
                  value={typed}
                  onChange={(e) => { if (!alreadyCorrect) setTyped(e.target.value); }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    answerState === "correct" ? "✅ Correct! Well done!" :
                    answerState === "wrong"   ? "❌ Wrong — try again!"  :
                    "Type your answer…"
                  }
                  disabled={alreadyCorrect}
                  autoComplete="off"
                />
                <button
                  className="game-submit-btn"
                  onClick={handleSubmit}
                  disabled={alreadyCorrect}
                >
                  {answerState === "correct" ? "✅" : answerState === "wrong" ? "❌" : "Submit"}
                </button>
              </div>

              {/* Feedback badge */}
              {answerState !== "idle" && (
                <div
                  className="game-feedback"
                  style={{
                    background: answerState === "correct"
                      ? "linear-gradient(135deg,rgba(52,211,153,0.18),rgba(16,185,129,0.12))"
                      : "linear-gradient(135deg,rgba(239,68,68,0.14),rgba(220,38,38,0.08))",
                    color:  answerState === "correct" ? "#059669" : "#dc2626",
                    border: `1.5px solid ${answerState === "correct" ? "rgba(52,211,153,0.4)" : "rgba(239,68,68,0.35)"}`,
                  }}
                >
                  {answerState === "correct"
                    ? <> ✅ Correct! &nbsp;<strong>+{pointsEarned} pts</strong>&nbsp; (Hint {revealCount}) </>
                    : <> ❌ Wrong answer — try again! </>
                  }
                </div>
              )}

              <div className="game-subtle">
                Hint level: {revealCount} &nbsp;·&nbsp;
                Points if correct now: <strong>{hintPoints[revealCount - 1]}</strong> &nbsp;·&nbsp;
                Total: {score}
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
